import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Box } from "@mui/material";
import moment from "moment";
import { globalStyle } from "../../global/Utils";
import { formatQty } from "../../global/stock";
import { formatTwoDecimals } from "../../global/money";

const Field = ({ label, value }) => (
  <div className="mb-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-sm font-semibold break-words">{value || "-"}</div>
  </div>
);

/** A saved line's item is populated; fall back to the snapshot on the line. */
const itemNameOf = (line) => {
  if (line?.itemName) return line.itemName;
  const value = line?.itemId;
  return value && typeof value === "object" ? value.name : "-";
};

const ViewReceiving = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const receiving = location.state;

  // The list navigates here with the row in state. Reached directly (a
  // bookmark, a refresh) there is nothing to show, so go back to the list.
  if (!receiving?._id) {
    return <Navigate to="/main/view-receiving-list" replace />;
  }

  const lines = receiving.items || [];

  return (
    <div
      className="p-6 animate-fade-in"
      style={{ fontFamily: globalStyle.fontFamily }}
    >
      <button
        onClick={() => navigate("/main/view-receiving-list")}
        className="mb-4 px-4 py-2 rounded text-white"
        style={{
          background: globalStyle.buttonColor,
          fontFamily: globalStyle.fontFamilyButton,
        }}
      >
        Back To List
      </button>

      <div className="space-y-6">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <div>
            <h2
              className="font-bold text-xl"
              style={{ color: globalStyle.curveDarkColor }}
            >
              {receiving.receivingNumber || "Receiving"}
            </h2>
            <p className="text-sm text-gray-500">
              {receiving.supplierName || "No supplier"}
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">Total Quantity</div>
            <div className="text-2xl font-bold">
              {formatQty(receiving.totalQuantity)}
            </div>
          </div>
        </Box>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
          <Field label="Date" value={receiving.date} />
          <Field label="Reference / Bill No." value={receiving.referenceNo} />
          <Field label="Company" value={receiving.companyId?.companyName} />
          <Field
            label="Total Cost"
            value={`Rs ${formatTwoDecimals(receiving.totalCost)}`}
          />
          <Field
            label="Created At"
            value={
              receiving.createdAt
                ? moment(receiving.createdAt).format("DD MMM YYYY, hh:mm A")
                : ""
            }
          />
          <Field
            label="Created By"
            value={
              receiving.createdBy?.userName || receiving.createdBy?.email
            }
          />
          <div className="md:col-span-3">
            <Field label="Note" value={receiving.note} />
          </div>
        </div>

        <div className="border-t pt-4">
          <h3
            className="font-bold mb-3"
            style={{ color: globalStyle.curveDarkColor }}
          >
            Items Received ({lines.length})
          </h3>

          {lines.length === 0 ? (
            <p className="text-sm text-gray-500">
              No items on this receiving.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr
                    style={{
                      background: globalStyle.blueColor,
                      color: "#fff",
                    }}
                  >
                    <th className="text-left px-3 py-2">#</th>
                    <th className="text-left px-3 py-2">Item</th>
                    <th className="text-left px-3 py-2">HS Code</th>
                    <th className="text-right px-3 py-2">Quantity</th>
                    <th className="text-left px-3 py-2">Unit</th>
                    <th className="text-right px-3 py-2">Unit Cost</th>
                    <th className="text-right px-3 py-2">Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => (
                    <tr key={line._id || index} className="border-b">
                      <td className="px-3 py-2">{index + 1}</td>
                      <td className="px-3 py-2 font-semibold">
                        {itemNameOf(line)}
                      </td>
                      <td className="px-3 py-2">{line.hsCode || "-"}</td>
                      <td className="px-3 py-2 text-right">
                        {formatQty(line.quantity)}
                      </td>
                      <td className="px-3 py-2">{line.uom || "-"}</td>
                      <td className="px-3 py-2 text-right">
                        {formatTwoDecimals(line.unitCost)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">
                        {formatTwoDecimals(line.lineTotal)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={() =>
              navigate("/main/edit-receiving", { state: receiving })
            }
            className="px-6 py-2 rounded text-white font-semibold"
            style={{ background: globalStyle.buttonColor }}
          >
            Edit Receiving
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewReceiving;
